import React, { useState, useEffect, useCallback } from 'react';
import { logError } from '@/lib/logger';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

import {
  CircleHeader,
  WeeklyPromptCard,
  CircleTabs,
  PostList,
  NewPostForm,
  ThreadList,
  NewThreadForm,
  ThreadDetailView
} from '@/components/circle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CirclePage = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('wall');
  const [selectedThreadId, setSelectedThreadId] = useState(searchParams.get('thread'));
  
  // Data state
  const [stats, setStats] = useState(null);
  const [weeklyPrompt, setWeeklyPrompt] = useState(null);
  const [posts, setPosts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  
  // Form state - Posts
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostAnonymous, setNewPostAnonymous] = useState(true);
  const [submittingPost, setSubmittingPost] = useState(false);
  
  // Form state - Threads
  const [showNewThread, setShowNewThread] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newThreadContent, setNewThreadContent] = useState('');
  const [newThreadAnonymous, setNewThreadAnonymous] = useState(false);
  const [submittingThread, setSubmittingThread] = useState(false);
  
  // Form state - Replies
  const [replyContent, setReplyContent] = useState('');
  const [replyAnonymous, setReplyAnonymous] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, postsRes, threadsRes, promptRes] = await Promise.all([
        axios.get(`${API}/circle/stats`, { headers }),
        axios.get(`${API}/circle/posts`, { headers }),
        axios.get(`${API}/circle/threads`, { headers }),
        axios.get(`${API}/circle/weekly-prompt`, { headers }).catch(() => null)
      ]);
      
      setStats(statsRes.data);
      setPosts(postsRes.data.posts || []);
      setThreads(threadsRes.data.threads || []);
      setWeeklyPrompt(promptRes?.data);
    } catch (error) {
      toast.error('Failed to load community');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    
    // Mark as visited
    const markVisited = async () => {
      if (token) {
        try {
          await axios.post(`${API}/circle/mark-visited`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          // Mark-visited failure is non-critical
                      logError('Mark visited failed:', error?.response?.status);
        }
      }
    };
    markVisited();
  }, [fetchData, token]);

  // Load thread detail if ID in URL
  const loadThread = useCallback(async (threadId) => {
    try {
      const response = await axios.get(`${API}/circle/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedThread(response.data);
    } catch (error) {
      toast.error('Thread not found');
      setSelectedThreadId(null);
      setSearchParams({});
              logError('Thread load failed:', error?.response?.status);
    }
  }, [token, setSearchParams]);

  useEffect(() => {
    if (selectedThreadId && threads.length > 0) {
      loadThread(selectedThreadId);
    }
  }, [selectedThreadId, threads, loadThread]);

  // Post actions
  const handleSubmitPost = useCallback(async () => {
    if (newPostContent.length < 10) {
      toast.error('Post must be at least 10 characters');
      return;
    }
    
    setSubmittingPost(true);
    try {
      const res = await axios.post(`${API}/circle/posts`, {
        content: newPostContent,
        is_anonymous: newPostAnonymous
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data?.pending_review) {
        toast.info('Thanks for sharing. Our moderators are giving this a quick look before it goes live.');
      } else {
        toast.success('Reflection shared');
      }
      setNewPostContent('');
      setShowNewPost(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    } finally {
      setSubmittingPost(false);
    }
  }, [newPostContent, newPostAnonymous, token, fetchData]);

  const handleLikePost = useCallback(async (postId) => {
    try {
      const res = await axios.post(`${API}/circle/posts/${postId}/heart`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPosts(prevPosts => prevPosts.map(p => {
        if (p.id === postId) {
          return {
            ...p,
            heart_count: res.data.heart_count,
            user_hearted: res.data.hearted
          };
        }
        return p;
      }));
    } catch (error) {
      // Like failure is non-critical - UI state will show optimistic update
              logError('Like action failed:', error?.response?.status);
    }
  }, [token]);

  const handleReportPost = useCallback(async (postId) => {
    if (!window.confirm('Report this post as inappropriate?')) return;
    
    try {
      await axios.post(`${API}/circle/posts/${postId}/report`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report submitted. Thank you.');
    } catch (error) {
      toast.error('Failed to submit report');
              logError('Report submit failed:', error?.response?.status);
    }
  }, [token]);

  // Thread actions
  const handleSubmitThread = useCallback(async () => {
    if (newThreadTitle.length < 5 || newThreadContent.length < 20) {
      toast.error('Title and content are required');
      return;
    }
    
    setSubmittingThread(true);
    try {
      await axios.post(`${API}/circle/threads`, {
        title: newThreadTitle,
        content: newThreadContent,
        is_anonymous: newThreadAnonymous
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Discussion created');
      setNewThreadTitle('');
      setNewThreadContent('');
      setShowNewThread(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discussion');
    } finally {
      setSubmittingThread(false);
    }
  }, [newThreadTitle, newThreadContent, newThreadAnonymous, token, fetchData]);

  const handleThreadClick = useCallback((threadId) => {
    setSelectedThreadId(threadId);
    setSearchParams({ thread: threadId });
    loadThread(threadId);
  }, [setSearchParams, loadThread]);

  const handleBackFromThread = useCallback(() => {
    setSelectedThreadId(null);
    setSelectedThread(null);
    setSearchParams({});
  }, [setSearchParams]);

  const handleSubmitReply = useCallback(async () => {
    if (replyContent.length < 10) {
      toast.error('Reply must be at least 10 characters');
      return;
    }
    
    setSubmittingReply(true);
    try {
      await axios.post(`${API}/circle/threads/${selectedThreadId}/replies`, {
        content: replyContent,
        is_anonymous: replyAnonymous
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Reply posted');
      setReplyContent('');
      loadThread(selectedThreadId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post reply');
    } finally {
      setSubmittingReply(false);
    }
  }, [replyContent, replyAnonymous, selectedThreadId, token, loadThread]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FBF9F6' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#C9A84C' }} />
      </div>
    );
  }

  // Thread detail view
  if (selectedThread) {
    return (
      <ThreadDetailView
        thread={selectedThread}
        onBack={handleBackFromThread}
        replyContent={replyContent}
        setReplyContent={setReplyContent}
        replyAnonymous={replyAnonymous}
        setReplyAnonymous={setReplyAnonymous}
        onSubmitReply={handleSubmitReply}
        submitting={submittingReply}
      />
    );
  }

  // Main view
  return (
    <div 
      className="min-h-screen pb-24"
      style={{ background: '#FBF9F6' }}
      data-testid="circle-page"
    >
      <div className="max-w-2xl mx-auto px-4 py-6">
        <CircleHeader stats={stats} />
        
        {/* Title */}
        <h1 
          className="text-2xl font-medium mb-2"
          style={{ color: '#1A1A2E', fontFamily: 'Cormorant Garamond, serif' }}
        >
          The Circle
        </h1>
        <p 
          className="text-sm mb-6"
          style={{ color: '#6B6878', fontFamily: 'Jost, sans-serif' }}
        >
          A private space for women walking the same path
        </p>

        <WeeklyPromptCard 
          weeklyPrompt={weeklyPrompt} 
          onRespond={() => setShowNewPost(true)} 
        />
        
        <CircleTabs activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Sharing Wall Tab */}
        {activeTab === 'wall' && (
          <>
            <NewPostForm
              show={showNewPost}
              setShow={setShowNewPost}
              content={newPostContent}
              setContent={setNewPostContent}
              isAnonymous={newPostAnonymous}
              setIsAnonymous={setNewPostAnonymous}
              onSubmit={handleSubmitPost}
              submitting={submittingPost}
            />
            <PostList 
              posts={posts} 
              onLike={handleLikePost}
              onReport={handleReportPost}
            />
          </>
        )}

        {/* Discussions Tab */}
        {activeTab === 'threads' && (
          <>
            <NewThreadForm
              show={showNewThread}
              setShow={setShowNewThread}
              title={newThreadTitle}
              setTitle={setNewThreadTitle}
              content={newThreadContent}
              setContent={setNewThreadContent}
              isAnonymous={newThreadAnonymous}
              setIsAnonymous={setNewThreadAnonymous}
              onSubmit={handleSubmitThread}
              submitting={submittingThread}
            />
            <ThreadList 
              threads={threads} 
              onThreadClick={handleThreadClick}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CirclePage;
